// Catalog.ID Client — the token service on the Bitcash v2 ledger
// ============================================================================
// The ONE place the CID client creates, lists and revokes a wallet's spending
// tokens (registration-wallets §4.12, on the v2 ledger: apps/bitcash/docs/plans/bitcash-v2.md
// M12, §4.10, D30, D52). Settings, the Connected Modules pane and the
// key-grant family all come through here, each having just come through a
// supersecret prompt or the organization's key wraps for the WALLET KEY.
//
//   create   the code is generated HERE and written into the account's
//            instruments blob (`cid/bitcash-instruments`) BEFORE
//            `POST /api/token/create` — the ledger never returns a code (T2),
//            and a retry with the same code is idempotent at the ledger;
//   list     `GET /api/wallet/tokens` with the VIEW code (refs and terms),
//            joined with the codes and labels the account kept — the ledger has
//            no labels, so `catalog:default` and `catalog:<module>` live only
//            in the blob; a token whose code this account never kept lists
//            with an empty `authToken` and can only be revoked, never handed out;
//   update   `POST /api/token/update {token, caps}` under the wallet key and the
//            token's own code: the caps replaced WHOLE, so day, month and service
//            types travel together (how the default token is widened, §9.3);
//   revoke   `POST /api/token/revoke {token}` under the wallet key;
//   ref      the wallet's v2 ref, computed here (no ledger call).
//
// THE PAYEE. `operator` is the member's DEFAULT token: minted UNBOUND — no payee
// at all, so one token pays every Operator's CPR and EFS — and scoped to the
// service types the caller passes, which are Catalog.ID's own
// (`Voucher:DefaultToken:ServiceTypes`; apps/docs/plans/second-node-and-bitcash-open-issues.md
// §2.1, §9.3). On the v2 ledger a token pays only an active Operator's wallet,
// never a P2P one, and every Operator is a division of one company, so leaving
// the payee out no longer lets it pay a third party. Its service types are what
// keeps it from paying CONTENT: an unbound auth token without them pays anything
// at any site, so `operator` without service types is refused here, before a
// code is written down. Any other payee is a receive address of an Operator
// wallet (D52), passed through and bound; anything else — a pilot walletRef above
// all — is refused.
//
// THE WALLET KEY IS A BEARER SECRET. It goes in `X-Wallet-Key` to the ledger
// and nowhere else; errors carry the ledger's message, never the key.

import { bitcashWorker, ledgerContext, type LedgerContext } from '../state/bitcash-context';
import {
    BitcashWorkerError as LedgerError, type BitcashWorker, type LedgerToken, type TokenCaps,
} from '../api/bitcash-worker';
import { bitToUnits, unitsToWholeBit } from '../api/bitcash-units';
import { newCode, verifyReceiveAddress, viewCodeOf, walletRefOf, TYPE_AUTH } from '../crypto/bitcash';
import { instrumentsOf, type TokenPurpose } from '../crypto/bitcash-instruments-doc';
import {
    defaultInstrumentsStoreDeps, loadInstruments, recordToken, type InstrumentsStoreDeps,
} from '../state/bitcash-instruments-store';

/** A token as a pane or a grant sees it: the ledger's terms, the account's code and label. */
export interface WorkerAuthToken {
    /** Display form (`AUTH-…`), what a module is handed; empty when this account never kept the code. */
    authToken: string;
    /** The token's ref on the ledger. */
    ref?: string;
    /** `catalog:default`, `catalog:<moduleId>`, `catalog:delegate:<cid>` — the account's own label. */
    label?: string;
    sites: string[];
    serviceTypes: string[];
    /** Unix ms, or null for a non-expiring token. */
    expiresAt: number | null;
    /** Whole BIT; null when the token has no such cap. */
    dailyCap: number | null;
    monthlyCap: number | null;
    createdAt?: number;
    /** Set once the token has been revoked; a revoked token is never adopted. */
    revokedAt?: number;
    /** The receive address the token is bound to, as the account recorded it; null for an unbound one. */
    payee?: string | null;
    /** The ledger's word on the binding: the payee's FUND ref, or null for an UNBOUND token. Absent
     *  when the listing did not say. The truth for "is it bound", where `payee` is the account's note. */
    payeeRef?: string | null;
    /** BIT spent through this token today and this month, when the ledger says. */
    daySpent?: number;
    monthSpent?: number;
}

export interface CreateAuthTokenInput {
    walletKey: string;
    label: string;
    sites: string[];
    serviceTypes: string[];
    /** Whole BIT. */
    dailyCap: number;
    monthlyCap: number;
    /** Unix ms, or null for no expiry. */
    expiresAt: number | null;
    /**
     * `"operator"` — the UNBOUND default token, scoped to `serviceTypes`, which must then be
     * Catalog.ID's configured list and never empty (§9.3) — or a receive address of an Operator
     * wallet (D52), which the token is bound to.
     */
    payee: 'operator' | string;
}

/** A token's terms as `token/update` takes them: the caps in full, whole BIT. */
export interface UpdateAuthTokenTerms {
    dailyCap: number;
    monthlyCap: number;
    serviceTypes: readonly string[];
}

export interface BitcashWorkerApi {
    listAuthTokens(walletKey: string): Promise<WorkerAuthToken[]>;
    createAuthToken(input: CreateAuthTokenInput): Promise<WorkerAuthToken>;
    /** Replace a token's caps WHOLE — day, month and service types — by its code. Free; the token
     *  keeps its code. A token whose code the account never kept cannot be updated. */
    updateAuthToken(walletKey: string, authToken: string, terms: UpdateAuthTokenTerms): Promise<void>;
    /** Revoke by the token's code, or by its ref when the account never kept the code. */
    revokeAuthToken(walletKey: string, authTokenOrRef: string): Promise<void>;
    /** The wallet's v2 ref, or null when the node names no ledger. */
    getWalletRef(walletKey: string): Promise<string | null>;
}

/**
 * A bare v2 service type (D55; libs/bitcash-code CATEGORY_PATTERN): the key of
 * the ledger's `RECORDS.serviceTypes` — `efs-ingest`, `efs-retrieve`,
 * `registry-write`, `registry-assetid`, `mail-…`. A token's `caps.serviceTypes`
 * takes exactly these, each once; anything else (the pilot's `EFS_INGEST`, an
 * upper-case or underscore form) the ledger refuses (400), and one outside
 * RECORDS it refuses `400 unknown_category`.
 */
export const SERVICE_TYPE_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;

/** True when `types` is a list of distinct bare service types, as `caps.serviceTypes` takes it. */
export function areServiceTypes(types: readonly string[]): boolean {
    return types.every(t => SERVICE_TYPE_PATTERN.test(t)) && new Set(types).size === types.length;
}

/**
 * A token's fixed expiry lies one day to ten years (365-day years) ahead of the
 * ledger's clock when `token/create` reaches it, bounds included
 * (apps/bitcash/worker src/tokens.ts `termsProblem`: `now + DAY_MS <= expiresAt
 * <= now + TERMS.lockMaxMs`); anything else is `400 bad_request`.
 */
export const TOKEN_EXPIRY_MIN_AHEAD_MS = 86_400_000;
export const TOKEN_EXPIRY_MAX_AHEAD_MS = 10 * 365 * 86_400_000;

/**
 * True when `expiresAt` (unix ms, as sent: whole milliseconds) lies within the
 * ledger's bounds of `now`. Checked against this device's clock: the ledger's
 * own `now` is later by the request's travel, so an expiry within that much of
 * one day ahead can still be refused there.
 */
export function isTokenExpiryInRange(expiresAt: number, now: number): boolean {
    if (!Number.isFinite(expiresAt) || !Number.isFinite(now)) return false;
    const sent = Math.floor(expiresAt);
    return sent >= now + TOKEN_EXPIRY_MIN_AHEAD_MS && sent <= now + TOKEN_EXPIRY_MAX_AHEAD_MS;
}

/** A refusal: the ledger's, or this service's own (no ledger, no payee). */
export class BitcashWorkerError extends Error {
    constructor(public readonly status: number, message: string, public readonly code: string | null = null) {
        super(message);
        this.name = 'BitcashWorkerError';
    }
}

export interface BitcashWorkerClientOptions {
    ledger?: BitcashWorker;
    context?: () => Promise<LedgerContext | null>;
    instruments?: InstrumentsStoreDeps;
    /** The clock a token's expiry is checked against (tests pin it). */
    now?: () => number;
}

/** The purpose a label names in the instruments blob. */
function purposeOf(label: string): TokenPurpose {
    if (label === 'catalog:default') return 'default';
    if (label.startsWith('catalog:delegate:')) return 'delegate';
    return 'module';
}

function wrap(err: unknown): never {
    if (err instanceof LedgerError) throw new BitcashWorkerError(err.status, err.message, err.code);
    throw err;
}

function numberOf(text: unknown): number | undefined {
    return typeof text === 'string' && /^[0-9]{1,16}$/.test(text) ? Number(text) : undefined;
}

export function createBitcashWorkerClient(options: BitcashWorkerClientOptions = {}): BitcashWorkerApi {
    const ledger = options.ledger ?? bitcashWorker();
    const context = options.context ?? ledgerContext;
    const instruments = options.instruments ?? defaultInstrumentsStoreDeps;
    const now = options.now ?? (() => Date.now());

    async function requireContext(): Promise<LedgerContext> {
        const ctx = await context();
        if (ctx === null) throw new BitcashWorkerError(0, 'This Catalog.ID server is not connected to a Bitcash ledger.', 'no_ledger');
        return ctx;
    }

    return {
        async listAuthTokens(walletKey: string): Promise<WorkerAuthToken[]> {
            const ctx = await requireContext();
            const listed = await ledger.walletTokens(viewCodeOf(walletKey, ctx.networkId)).catch(wrap);
            const kept = instrumentsOf((await loadInstruments(instruments))?.doc ?? { v: 1, wallets: {} },
                walletRefOf(walletKey, ctx.networkId)).tokens;
            const byRef = new Map(kept.map(t => [walletRefOf(t.code, ctx.networkId), t]));
            return listed.tokens.filter((t: LedgerToken) => t.kind === 'auth').map((t: LedgerToken) => {
                const own = byRef.get(t.ref);
                const caps = (t.caps ?? {}) as Record<string, unknown>;
                const used = (t as { used?: { day?: string; month?: string } }).used;
                return {
                    authToken: own?.code ?? '',
                    ref: t.ref,
                    ...(own ? { label: own.label, payee: own.payee } : {}),
                    ...(t.payeeRef !== undefined ? { payeeRef: t.payeeRef } : {}),
                    sites: [],
                    serviceTypes: Array.isArray(caps.serviceTypes) ? caps.serviceTypes as string[] : [],
                    expiresAt: numberOf(caps.expiresAt) ?? null,
                    dailyCap: unitsToWholeBit(caps.day as string | undefined),
                    monthlyCap: unitsToWholeBit(caps.month as string | undefined),
                    createdAt: numberOf(t.createdAt),
                    ...(t.revokedAt ? { revokedAt: numberOf(t.revokedAt) } : {}),
                    ...(used?.day !== undefined ? { daySpent: unitsToWholeBit(used.day) ?? undefined } : {}),
                    ...(used?.month !== undefined ? { monthSpent: unitsToWholeBit(used.month) ?? undefined } : {}),
                };
            });
        },

        async createAuthToken(input: CreateAuthTokenInput): Promise<WorkerAuthToken> {
            // Passed EXACTLY as given, so checked BEFORE the code is written down:
            // the ledger would refuse the token after the blob already held it.
            if (!areServiceTypes(input.serviceTypes)) {
                throw new BitcashWorkerError(0, 'A token\'s service types are the ledger\'s bare service types '
                    + '(efs-ingest, registry-write, …), each named once (apps/bitcash/docs/plans/bitcash-v2.md D55).', 'bad_service_type');
            }
            // Same reason: the ledger refuses an expiry outside its bounds after the code is kept.
            if (input.expiresAt !== null && !isTokenExpiryInRange(input.expiresAt, now())) {
                throw new BitcashWorkerError(0, 'A token\'s expiry lies one day to ten years ahead; the ledger refuses '
                    + 'any other.', 'bad_token_expiry');
            }
            let payee: string | null;
            if (input.payee === 'operator') {
                // The unbound default token. Without service types it would also pay content,
                // at any site: never minted (§9.3 D-a, fail closed).
                if (input.serviceTypes.length === 0) {
                    throw new BitcashWorkerError(0, 'The default token is minted without a payee, so it must name the service '
                        + 'types it pays; this Catalog.ID server names none (Voucher:DefaultToken:ServiceTypes).', 'unscoped_unbound');
                }
                payee = null;
            } else if (verifyReceiveAddress(input.payee)) {
                payee = input.payee;
            } else {
                throw new BitcashWorkerError(0, 'A token pays a receive address of an Operator (apps/bitcash/docs/plans/bitcash-v2.md D52); '
                    + 'this request named something else.', 'payee_unsupported');
            }
            const ctx = await requireContext();
            const caps: TokenCaps = {
                day: bitToUnits(input.dailyCap),
                month: bitToUnits(input.monthlyCap),
                ...(input.serviceTypes.length > 0 ? { serviceTypes: input.serviceTypes } : {}),
                ...(input.expiresAt !== null ? { expiresAt: String(Math.floor(input.expiresAt)) } : {}),
            };
            const code = newCode(TYPE_AUTH);
            // Written down FIRST: if the ledger call is lost, a retry finds this code.
            await recordToken(walletRefOf(input.walletKey, ctx.networkId),
                { code, purpose: purposeOf(input.label), label: input.label, payee }, instruments);
            await ledger.createToken({
                walletKey: input.walletKey, token: code, ...(payee !== null ? { payee } : {}), caps,
                ...(input.sites.length > 0 ? { sites: input.sites } : {}),
            }).catch(wrap);
            return {
                authToken: code,
                ref: walletRefOf(code, ctx.networkId),
                label: input.label,
                sites: input.sites,
                serviceTypes: input.serviceTypes,
                expiresAt: input.expiresAt,
                dailyCap: input.dailyCap,
                monthlyCap: input.monthlyCap,
                createdAt: Date.now(),
                payee,
                payeeRef: payee === null ? null : walletRefOf(payee, ctx.networkId),
            };
        },

        async updateAuthToken(walletKey: string, authToken: string, terms: UpdateAuthTokenTerms): Promise<void> {
            if (!authToken) {
                throw new BitcashWorkerError(0, 'A token is updated by its own code, which this account did not keep.', 'token_code_unknown');
            }
            // The caps are replaced whole, so an empty list would leave an unbound token unscoped.
            if (terms.serviceTypes.length === 0 || !areServiceTypes(terms.serviceTypes)) {
                throw new BitcashWorkerError(0, 'A token\'s service types are the ledger\'s bare service types '
                    + '(efs-ingest, registry-write, …), each named once, and at least one.', 'bad_service_type');
            }
            await requireContext();
            await ledger.updateToken({
                walletKey, token: authToken,
                caps: { day: bitToUnits(terms.dailyCap), month: bitToUnits(terms.monthlyCap), serviceTypes: [...terms.serviceTypes] },
            }).catch(wrap);
        },

        async revokeAuthToken(walletKey: string, authTokenOrRef: string): Promise<void> {
            const which = /^[0-9a-f]{64}$/.test(authTokenOrRef) ? { ref: authTokenOrRef } : { token: authTokenOrRef };
            await ledger.revokeToken(walletKey, which).catch(wrap);
        },

        async getWalletRef(walletKey: string): Promise<string | null> {
            const ctx = await context();
            return ctx === null ? null : walletRefOf(walletKey, ctx.networkId);
        },
    };
}
